import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import Fade from '@bexs/dollar/components/Fade';
import { ApplicationState } from 'store';
import authActions from 'store/ducks/auth/actions';
import { AuthState } from 'store/ducks/auth/types';
import ErrorHandler from 'client/ErrorHandler';
import {
  LoginHeader,
  Container,
  LoginWrapper,
  LoginInner,
  InputWrap,
  Button,
  ProgressLoading,
  ErrorLabel,
  ErrorMessage,
} from './styles';

const Login: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Invalid e-mail format')
        .required('Enter your e-mail'),
      password: Yup.string().required('Enter your password'),
    }),
    onSubmit: ({ email, password }) => {
      dispatch(
        authActions.request({
          email,
          password,
        })
      );
    },
  });

  const { isLoading, error, userInfo } = useSelector<
    ApplicationState,
    AuthState
  >((state) => state.auth);

  const errorMessage = useMemo(() => {
    return error ? ErrorHandler(error) : null;
  }, [error]);

  useEffect(() => {
    if (userInfo) {
      //temporary solution to reload the page after login
      window.location.reload();
      history.push('/');
    }
  }, [history, userInfo]);

  return (
    <>
      <LoginHeader>
        <img
          src="https://storage.googleapis.com/bexs-forex-static/images/ebury-bank-logo-black-horizontal.png"
          alt="Ebury Logo"
          title="Ebury"
        />
      </LoginHeader>
      <Fade in={!!error}>
        <ErrorMessage data-test="error-message">{errorMessage}</ErrorMessage>
      </Fade>

      <Container>
        <LoginWrapper>
          <h1>Log into your account</h1>
          <LoginInner>
            <form onSubmit={formik.handleSubmit}>
              <InputWrap
                isError={!!formik.touched.email && !!formik.errors.email}
                isFilled={formik.values.email !== ''}
              >
                <input
                  type="text"
                  id="email"
                  {...formik.getFieldProps('email')}
                />
                <label htmlFor="email">E-mail</label>
                <Fade
                  in={!!formik.touched.email && !!formik.errors.email}
                  timeout={0}
                >
                  <ErrorLabel>Enter your e-mail</ErrorLabel>
                </Fade>
              </InputWrap>

              <InputWrap
                style={{
                  marginTop: '48px',
                }}
                isError={!!formik.touched.password && !!formik.errors.password}
                isFilled={formik.values.password !== ''}
              >
                <input
                  type="password"
                  id="password"
                  {...formik.getFieldProps('password')}
                />
                <label htmlFor="password">Password</label>
                <Fade
                  in={!!formik.touched.password && !!formik.errors.password}
                  timeout={0}
                >
                  <ErrorLabel>Enter your password</ErrorLabel>
                </Fade>
              </InputWrap>
              <div
                style={{
                  position: 'relative',
                }}
              >
                <Button isLoading={isLoading} type="submit">
                  Login
                </Button>
                <ProgressLoading isLoading={isLoading} />
              </div>
            </form>
          </LoginInner>
        </LoginWrapper>
      </Container>
    </>
  );
};

export default Login;
